import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';

import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import SettingsRedirectModal from "@/components/ChatComponent/SettingsRedirectModal";
import loanApplicationService from "@/service/loanApplication.service";
import customerAuthService from "@/service/auth.service/customer.auth.service";
import { useSelector } from "react-redux";
import api from "@/modules/customer/service/interceptor";
import { toast } from "react-toastify";

const DocumentsSection = () => {
  const [documents, setDocuments] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [verifiedDocuments, setVerifiedDocuments] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const customerId = useSelector((state) => state.auth.auth._id);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!customerId) {
      return;
    }

    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/customers/${customerId}/documents`);
        
        // Check if response is successful
        if (response.data.data && response.data.status === true && response.data.data.data) {
          const data = response.data.data.data;
          if (data.requires_2fa) {
            setRequires2FA(true);
            // Format documents without URLs
            const formatted = (data.documents || []).map((doc) => ({
              id: doc.s3_url_path, // Use s3_url_path as unique identifier
              filename: doc.original_name,
              filetype: doc.file_type,
              uploadDate: new Date(doc.uploaded_at).toLocaleDateString(),
              fileUrl: null, // No URL available without 2FA
              fileSize: formatBytes(doc.size || 0),
            }));
            setDocuments(formatted);
          } else {
            setRequires2FA(false);
            // Format documents with URLs
            const formatted = (data.documents || []).map((doc) => ({
              id: doc.s3_url_path, // Use s3_url_path as unique identifier
              filename: doc.original_name,
              filetype: doc.file_type,
              uploadDate: new Date(doc.uploaded_at).toLocaleDateString(),
              fileUrl: doc.file_url,
              fileSize: formatBytes(doc.size || 0),
            }));
            setDocuments(formatted);
          }
        } else {
          // Fallback to old API structure if new structure is not available
          const token = localStorage.getItem("access_token");
          const userData = await customerAuthService.fetchUserData(customerId, token);
          const formatted = (userData.documents || []).map((doc) => ({
            id: doc.s3_url_path, // Use s3_url_path as unique identifier
            filename: doc.original_name,
            filetype: doc.file_type,
            uploadDate: new Date(doc.uploaded_at).toLocaleDateString(),
            fileUrl: doc.file_url,
            fileSize: formatBytes(doc.size),
          }));
          setDocuments(formatted);
          setRequires2FA(false);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching documents:", error);
        console.error("Error details:", error.response?.data);
        console.error("Error status:", error.response?.status);
        
        // Try fallback method on error
        try {
          const token = localStorage.getItem("access_token");
          const userData = await customerAuthService.fetchUserData(customerId, token);
          const formatted = (userData.documents || []).map((doc) => ({
            id: doc.s3_url_path, // Use s3_url_path as unique identifier
            filename: doc.original_name,
            filetype: doc.file_type,
            uploadDate: new Date(doc.uploaded_at).toLocaleDateString(),
            fileUrl: doc.file_url,
            fileSize: formatBytes(doc.size),
          }));
          setDocuments(formatted);
          setRequires2FA(false);
        } catch (fallbackError) {
          console.error("Fallback method also failed:", fallbackError);
          toast.error("Failed to fetch documents");
        }
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [customerId]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDelete = async (id, filename) => {
    try {
      await loanApplicationService.deleteDocument(id, filename);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      setOpenDeleteModal(false);
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const handle2FAVerification = async () => {
    setError('');
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      // toast.error("Please enter a valid 6-digit code");
      return;
    }

    try {
      setIsVerifying(true);
      
      if (pendingAction) {
        // Use the new single document verification API
        const { action, document } = pendingAction;
        
        const response = await api.post(`/customers/${customerId}/onedocuments/verify?s3_path=${encodeURIComponent(document.id)}&verification_code=${verificationCode}`);
        
        if (response.data.status && response.data.data.document) {
          const verifiedDoc = response.data.data.document;
          
          // Update the specific document in the documents array
          setDocuments(prevDocs => 
            prevDocs.map(doc => 
              doc.id === document.id 
                ? { ...doc, fileUrl: verifiedDoc.file_url }
                : doc
            )
          );
          
          setShow2FADialog(false);
          setVerificationCode('');
          setPendingAction(null);
          
          // Execute the action (view or download)
          if (action === 'view') {
            window.open(verifiedDoc.file_url, "_blank");
          } else if (action === 'download') {
            window.open(verifiedDoc.file_url, "_blank");
          }
          
          toast.success("2FA verification successful! Document accessed.");
        }
      } else {
        // Fallback to old API for bulk verification
        const response = await api.post(`/customers/${customerId}/documents/verify`, {
          verification_code: verificationCode
        });
        
        const verifiedDocs = response.data.data.documents.map((doc) => ({
          id: doc.s3_url_path, // Use s3_url_path as unique identifier
          filename: doc.original_name,
          filetype: doc.file_type,
          uploadDate: new Date(doc.uploaded_at).toLocaleDateString(),
          fileUrl: doc.file_url,
          fileSize: formatBytes(doc.size || 0),
        }));
        
        setVerifiedDocuments(verifiedDocs);
        setDocuments(verifiedDocs);
        setShow2FADialog(false);
        setVerificationCode('');
        toast.success("2FA verification successful! You can now access documents.");
      }
    } catch (error) {
      setError('Invalid verification code. Please try again');
      // toast.error("Invalid verification code. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const executePendingAction = () => {
    if (pendingAction) {
      const { action, document } = pendingAction;
      if (action === 'view') {
        window.open(document.fileUrl, "_blank");
      } else if (action === 'download') {
        window.open(document.fileUrl, "_blank");
      }
      setPendingAction(null);
    }
  };

  const handleViewDocument = (document) => {
    if (requires2FA) {
      setPendingAction({ action: 'view', document });
      setShow2FADialog(true);
    } else {
      // Show settings modal when 2FA is not enabled
      setShowSettingsModal(true);
    }
  };

  const handleDownloadDocument = (document) => {
    if (requires2FA) {
      setPendingAction({ action: 'download', document });
      setShow2FADialog(true);
    } else {
      // Show settings modal when 2FA is not enabled
      setShowSettingsModal(true);
    }
  };


  return (
    <Box p={1} sx={{width:"100%"}}>
      {isLoading ? (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="200px"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading documents...
          </Typography>
        </Box>
      ) : (
        <>
          {requires2FA && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              🔒 Two-Factor Authentication is enabled. Click on view/download buttons to verify and access documents.
            </Alert>
          )}
          <Table
        sx={{
          borderCollapse: "separate",
          borderSpacing: "0 12px", // horizontal = 0, vertical = 12px (space between rows)
        }}
      >
        <TableHead>
          <TableRow >
            <TableCell><strong>Filename</strong></TableCell>
            <TableCell><strong>Filetype</strong></TableCell>
            <TableCell><strong>Upload Date</strong></TableCell>
            <TableCell><strong>Filesize</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No documents found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            documents.map((doc) => (
            <TableRow
              key={doc.id}
              sx={{ backgroundColor: "#f9f9ff", "&:nth-of-type(odd)": { backgroundColor: "#f7f8fc", mb:5 } }}
            >
              <TableCell sx={{ maxWidth: 200, whiteSpace: "normal", wordBreak: "break-word" }}>
                <Tooltip title={doc.filename} arrow>
                  <Typography variant="body2">{doc.filename}</Typography>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ maxWidth: 150, whiteSpace: "normal", wordBreak: "break-word" }}>
                <Tooltip title={doc.filetype} arrow>
                  <Typography variant="body2">{doc.filetype}</Typography>
                </Tooltip>
              </TableCell>
              <TableCell>{doc.uploadDate}</TableCell>
              <TableCell>{doc.fileSize}</TableCell>
              <TableCell>
                <Tooltip title={doc.fileUrl ? "View" : "2FA Required - Click to verify"}>
                  <IconButton 
                    onClick={() => handleViewDocument(doc)}
                    disabled={false}
                  >
                    <RemoveRedEyeOutlinedIcon 
                      color={doc.fileUrl ? "primary" : "action"} 
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title={doc.fileUrl ? "Download" : "2FA Required - Click to verify"}>
                  <IconButton 
                    onClick={() => handleDownloadDocument(doc)}
                    disabled={false}
                  >
                    <DownloadIcon 
                      color={doc.fileUrl ? "primary" : "action"} 
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => {
                    setDocumentToDelete(doc);
                    setOpenDeleteModal(true);
                  }}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onDelete={handleDelete}
        documentId={documentToDelete?.id}
        filename={documentToDelete?.filename}
      />

      {/* Settings Redirect Modal */}
      <SettingsRedirectModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        userRole="customer"
      />

      {/* 2FA Verification Dialog */}
      <Dialog
        open={show2FADialog}
        onClose={() => setShow2FADialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>2FA Verification Required</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Two-Factor Authentication is enabled. Please enter your 6-digit verification code to access documents.
          </Alert>
          
          {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
          <TextField
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="123456"
            inputProps={{ maxLength: 6 }}
            fullWidth
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShow2FADialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handle2FAVerification}
            disabled={isVerifying || verificationCode.length !== 6}
            startIcon={isVerifying ? <CircularProgress size={20} /> : null}
          >
            {isVerifying ? 'Verifying...' : 'Verify & Access'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default DocumentsSection;
